import React, {FC, useEffect, useMemo, useState} from 'react';

import {
  globalWindow,
  IframeFeaturePoliciesEnum,
} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {addQueryToUrl} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {teammateToPluginJSON} from '../../helpers/pluginHelpers';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {
  onTrackIntegrationMouseEvent,
  SidePanelFrameMouseTrackingValuesEnum,
} from '../../helpers/sidePanelTrackingHelpers';
import {ApplicationModel} from '../../models/api/applicationModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {Iframe} from '../common/iframe';
import {useFrameOnMessage, useWasVisibleOnce} from '../inboxes/side/sideFrameHooks';
import {AppEntrypointLegacySdkContainerProps} from './appEntryPointLegacySdkContainer';

/*
 * Props.
 */

interface AppEntrypointLegacySdkComponentProps extends AppEntrypointLegacySdkContainerProps {
  user: TeammateModel;
  isUserGlobalAdmin: boolean;
  isVisible?: boolean;
  application: ApplicationModel;
}

/*
 * Component.
 */

export const AppEntrypointLegacySdkComponent: FC<AppEntrypointLegacySdkComponentProps> = (props) => {
  const {user, isUserGlobalAdmin, entryPoint, isVisible, trackingPayload, application} = props;
  const {action} = entryPoint;
  const [isReady, setIsReady] = useState(false);
  const {isConnected, onMessage, sendDataToIframe, dialogs} = useFrameOnMessage({
    ...props,
    isReady,
    targetOrigin: extractOrigin(action?.endpoint || ''),
    dispatch: useAppDispatch(),
  });

  /*
   * Send data once connected.
   */

  // User.
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    sendDataToIframe({
      type: 'user',
      user: teammateToPluginJSON(user, isUserGlobalAdmin),
    });

    setIsReady(true);
  }, [isConnected, user, isUserGlobalAdmin, sendDataToIframe]);

  // IsVisible.
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    sendDataToIframe({type: 'panel_visible', visible: Boolean(isVisible)});
  }, [isConnected, isVisible, sendDataToIframe]);

  /*
   * Tracking.
   */

  const onTrackMouse = (type: SidePanelFrameMouseTrackingValuesEnum) => {
    if (!trackingPayload) {
      return;
    }
    onTrackIntegrationMouseEvent(type, entryPoint.name, trackingPayload);
  };

  /*
   * Iframe render.
   */

  const iframeEndpoint = useMemo(
    () => action && buildIframeEndpoint(action.endpoint, application.installationKey),
    [action, application],
  );

  // No need to render the iFrame if we weren't visible at least once.
  const wasVisibleOnce = useWasVisibleOnce(props.isVisible);
  if (!wasVisibleOnce || !iframeEndpoint) {
    return null;
  }

  return (
    <>
      {dialogs}
      <Iframe
        isVisible={isVisible}
        endpoint={iframeEndpoint}
        title={entryPoint.name}
        onMessage={onMessage}
        onMouseEnter={() => onTrackMouse(SidePanelFrameMouseTrackingValuesEnum.MOUSE_ENTER)}
        onMouseLeave={() => onTrackMouse(SidePanelFrameMouseTrackingValuesEnum.MOUSE_LEAVE)}
        featurePolicies={[IframeFeaturePoliciesEnum.CAMERA, IframeFeaturePoliciesEnum.MICROPHONE]}
      />
    </>
  );
};

/*
 * Helpers.
 */

function buildIframeEndpoint(endpoint: string, authSecret?: string) {
  try {
    return addQueryToUrl(endpoint, {
      auth_secret: authSecret,
      front_domain: globalWindow.location.origin,
    });
  } catch (error) {
    // This shouldn't happen.
    reportToBugsnag(error);
    return endpoint;
  }
}

function extractOrigin(endpoint: string) {
  try {
    const domain = new URL(endpoint);
    return domain.origin;
  } catch (err) {
    reportToBugsnag(err);
    return '';
  }
}
